import React, { useState } from "react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useI18n } from "@/contexts/I18nContext";
import { api, formatApiError } from "@/lib/api";
import {
  IconBrain, IconSchool, IconBuildingCommunity, IconHandStop,
  IconCalculator, IconPlayerPlay, IconRefresh, IconCheck, IconArrowLeft,
  IconScale,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";

// -------- Question banks (bilingual) --------
const QUESTIONS = {
  tdah: {
    icon: <IconBrain size={22} />,
    steps: 6,
    scale: [
      { v: 0, es: "Nunca", en: "Never" },
      { v: 1, es: "A veces", en: "Sometimes" },
      { v: 2, es: "Frecuente", en: "Often" },
      { v: 3, es: "Casi siempre", en: "Almost always" },
    ],
    q: [
      { es: "Se distrae con facilidad en tareas cotidianas.", en: "Gets easily distracted in daily tasks." },
      { es: "Le cuesta terminar lo que empieza sin recordatorios.", en: "Struggles to finish what they start without reminders." },
      { es: "Se mueve o habla más que otros niños de su edad.", en: "Moves or talks more than peers." },
      { es: "Actúa antes de pensar en las consecuencias.", en: "Acts before thinking of consequences." },
      { es: "Olvida instrucciones simples con frecuencia.", en: "Frequently forgets simple instructions." },
      { es: "Le cuesta esperar su turno.", en: "Struggles to wait their turn." },
    ],
  },
  "aula-signals": {
    icon: <IconSchool size={22} />,
    steps: 6,
    yesno: true,
    q: [
      { es: "Se cansa muy rápido en tareas de lectura o escritura.", en: "Tires quickly in reading/writing tasks." },
      { es: "Evita participar frente al grupo.", en: "Avoids participating in front of the group." },
      { es: "Muestra ansiedad ante evaluaciones o cambios.", en: "Shows anxiety before tests or changes." },
      { es: "Se aisla o parece 'apagarse' en momentos concretos.", en: "Withdraws or 'shuts down' at specific moments." },
      { es: "Sus notas no reflejan lo que sabe verbalmente.", en: "Grades don't reflect what they know verbally." },
      { es: "Presenta explosiones emocionales por cosas pequeñas.", en: "Has emotional outbursts over small things." },
    ],
  },
  "inclusion-school": {
    icon: <IconBuildingCommunity size={22} />,
    steps: 6,
    scale: [
      { v: 0, es: "Nada", en: "Not at all" },
      { v: 1, es: "Poco", en: "A little" },
      { v: 2, es: "Regular", en: "Somewhat" },
      { v: 3, es: "Bastante", en: "Quite a bit" },
      { v: 4, es: "Totalmente", en: "Totally" },
    ],
    q: [
      { es: "El centro tiene un protocolo de inclusión escrito y vigente.", en: "The school has a written, active inclusion protocol." },
      { es: "Todos los estudiantes con adecuación tienen su plan documentado.", en: "All students with accommodations have documented plans." },
      { es: "Los docentes reciben formación regular en neurodivergencia.", en: "Teachers receive regular neurodivergence training." },
      { es: "Existe un equipo de apoyo psicoeducativo activo.", en: "There is an active psychoeducational support team." },
      { es: "Las familias participan de forma real en el proceso.", en: "Families genuinely participate in the process." },
      { es: "El clima escolar celebra las diferencias en el aprendizaje.", en: "The school climate celebrates learning differences." },
    ],
  },
  "shadow-need": {
    icon: <IconHandStop size={22} />,
    steps: 5,
    yesno: true,
    q: [
      { es: "Tu hijo necesita apoyo constante para permanecer en la tarea.", en: "Your child needs constant support to stay on task." },
      { es: "El docente ha expresado que no puede sostener el nivel de apoyo requerido.", en: "The teacher has said they can't sustain the needed support." },
      { es: "Presenta desregulación frecuente que interrumpe el aula.", en: "Shows frequent dysregulation that disrupts class." },
      { es: "Tiene un diagnóstico o evaluación reciente.", en: "Has a recent diagnosis or evaluation." },
      { es: "Se han intentado adecuaciones y no han sido suficientes.", en: "Accommodations have been tried and are not enough." },
    ],
  },
};

const GADGETS = [
  { id: "autodiag-seis", icon: <IconScale size={22} />, href: "/autodiagnostico-seis-areas", t: { es: "Autodiagnóstico del equilibrio docente", en: "Teacher balance self-check" }, d: { es: "6 áreas · 18 preguntas · 5 min. Modelo Maslach & Leiter.", en: "6 areas · 18 questions · 5 min. Maslach & Leiter model." } },
  { id: "tdah", icon: <IconBrain size={22} />, t: { es: "Tamizaje de TDAH para padres", en: "ADHD screening for parents" }, d: { es: "6 preguntas sobre tu hijo, orientación clara.", en: "6 questions about your child, clear orientation." } },
  { id: "aula-signals", icon: <IconSchool size={22} />, t: { es: "Autochequeo de señales en el aula", en: "Classroom signals self-check" }, d: { es: "Para docentes: marca lo que observas.", en: "For teachers: check what you observe." } },
  { id: "inclusion-school", icon: <IconBuildingCommunity size={22} />, t: { es: "¿Qué tan inclusiva es tu escuela?", en: "How inclusive is your school?" }, d: { es: "Para directivos: 6 preguntas sobre tu centro.", en: "For principals: 6 questions about your school." } },
  { id: "shadow-need", icon: <IconHandStop size={22} />, t: { es: "¿Tu hijo necesita una maestra sombra?", en: "Does your child need a shadow teacher?" }, d: { es: "5 preguntas para orientarte.", en: "5 questions to orient you." } },
  { id: "adec-calc", icon: <IconCalculator size={22} />, t: { es: "Calculadora de adecuaciones", en: "Accommodation calculator" }, d: { es: "Elige edad, condición y área. Recibe sugerencias.", en: "Choose age, condition and area. Get suggestions." } },
];

export default function SiteGadgets() {
  const { t, lang } = useI18n();
  const [active, setActive] = useState(null);

  return (
    <div className="bg-paper">
      <SiteNav />
      <section className="py-16">
        <div className="wrap">
          {active ? (
            <ActiveGadget id={active} onBack={() => setActive(null)} />
          ) : (
            <>
              <div className="mb-10">
                <p className="eyebrow">{t("gadgets.eye")}</p>
                <h1 className="font-display font-medium text-[clamp(28px,4vw,40px)] mt-2">
                  {t("gadgets.title")}
                </h1>
                <p className="text-muted text-[14.5px] mt-3 max-w-[36em]">{t("gadgets.lead")}</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {GADGETS.map((g) => (
                  <div key={g.id} className="mod-card" data-testid={`gadget-card-${g.id}`}>
                    <div className="w-11 h-11 rounded-xl bg-teal-soft text-teal-deep flex items-center justify-center mb-3">
                      {g.icon}
                    </div>
                    <span className="tag">PsicoGadget</span>
                    <h3 className="font-display text-[19px] font-medium mt-2">{g.t[lang]}</h3>
                    <p className="text-muted text-[13.5px] mt-2 leading-relaxed">{g.d[lang]}</p>
                    {g.href ? (
                      <Link
                        className="btn mt-5"
                        to={g.href}
                        data-testid={`gadget-open-${g.id}`}
                      >
                        <IconPlayerPlay size={14} /> {t("gadgets.free")}
                      </Link>
                    ) : (
                      <button
                        className="btn mt-5"
                        onClick={() => setActive(g.id)}
                        data-testid={`gadget-start-${g.id}`}
                      >
                        <IconPlayerPlay size={14} /> {t("gadgets.free")}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-14 pt-10 border-t border-line text-center">
                <p className="text-tert text-[13px]">{t("gadgets.cross")}</p>
                <a
                  href="https://dataismo.ai/campus/"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost btn mt-4 inline-flex"
                >
                  {t("gadgets.cross_btn")}
                </a>
              </div>
            </>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

// ----------- Runner -----------
function ActiveGadget({ id, onBack }) {
  const { t, lang } = useI18n();
  if (id === "adec-calc") return <AdecuacionesCalc onBack={onBack} />;
  return <QuestionRunner id={id} onBack={onBack} lang={lang} t={t} />;
}

function QuestionRunner({ id, onBack, lang, t }) {
  const cfg = QUESTIONS[id];
  const gadget = GADGETS.find((g) => g.id === id);
  const [answers, setAnswers] = useState(Array(cfg.q.length).fill(null));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const done = answers.every((a) => a !== null);

  const submit = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/gadgets/submit", { gadget_id: id, answers, lang });
      setResult(data.result);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  };

  const restart = () => { setAnswers(Array(cfg.q.length).fill(null)); setResult(null); };

  return (
    <div className="max-w-[720px] mx-auto">
      <button className="btn-ghost btn mb-6" onClick={onBack} data-testid="gadget-back">
        <IconArrowLeft size={14} /> {t("nav.gadgets")}
      </button>
      <div className="flex items-start gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-teal-soft text-teal-deep flex items-center justify-center">{cfg.icon}</div>
        <div>
          <p className="eyebrow">PsicoGadget</p>
          <h1 className="font-display text-[26px] font-medium">{gadget.t[lang]}</h1>
        </div>
      </div>
      <p className="text-muted text-[13.5px] mb-6">{gadget.d[lang]}</p>

      {!result && (
        <div className="space-y-4">
          {cfg.q.map((q, i) => (
            <div key={i} className="card" data-testid={`gadget-q-${id}-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="font-mono text-[10.5px] text-tert uppercase tracking-widest">
                  {String(i + 1).padStart(2, "0")}/{String(cfg.q.length).padStart(2, "0")}
                </span>
              </div>
              <p className="text-[14.5px] mb-3">{q[lang]}</p>
              <div className="flex flex-wrap gap-2">
                {cfg.yesno ? (
                  [true, false].map((v) => (
                    <button
                      key={String(v)}
                      className={`chip ${answers[i] === v ? "sel" : ""}`}
                      onClick={() => { const na = [...answers]; na[i] = v; setAnswers(na); }}
                      data-testid={`gadget-a-${id}-${i}-${v ? "yes" : "no"}`}
                    >
                      {v ? (lang === "es" ? "Sí" : "Yes") : (lang === "es" ? "No" : "No")}
                    </button>
                  ))
                ) : (
                  cfg.scale.map((opt) => (
                    <button
                      key={opt.v}
                      className={`chip ${answers[i] === opt.v ? "sel" : ""}`}
                      onClick={() => { const na = [...answers]; na[i] = opt.v; setAnswers(na); }}
                      data-testid={`gadget-a-${id}-${i}-${opt.v}`}
                    >
                      {opt[lang]}
                    </button>
                  ))
                )}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <button className="btn" disabled={!done || loading} onClick={submit} data-testid={`gadget-finish-${id}`}>
              <IconCheck size={14} /> {loading ? "..." : t("gadgets.finish")}
            </button>
            <button className="btn-ghost btn" onClick={restart}>
              <IconRefresh size={14} /> {t("gadgets.restart")}
            </button>
          </div>
          {error && <p className="text-errtext text-[13px]">{error}</p>}
        </div>
      )}

      {result && (
        <div className="card" data-testid={`gadget-result-${id}`}>
          <p className="eyebrow">Resultado · {result.percent}%</p>
          <div className="track mt-3"><div className="fill" style={{ width: `${result.percent}%` }} /></div>
          <div className="flex items-center gap-2 mt-4">
            <span className={`tag ${result.band === "alta" ? "tag-honey" : result.band === "media" ? "" : ""}`}>
              {t(`gadgets.band.${result.band}`)}
            </span>
            <span className="text-tert text-[12px]">{result.score}/{result.max}</span>
          </div>
          <p className="text-[14px] mt-4 leading-relaxed">
            {lang === "es" ? result.message_es : result.message_en}
          </p>
          <div className="flex gap-3 mt-6">
            <button className="btn-ghost btn" onClick={restart}><IconRefresh size={14} /> {t("gadgets.restart")}</button>
            <button className="btn" onClick={onBack}>{t("nav.gadgets")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdecuacionesCalc({ onBack }) {
  const { t, lang } = useI18n();
  const [age, setAge] = useState(8);
  const [condition, setCondition] = useState("TDAH");
  const [area, setArea] = useState("Lectura");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/gadgets/adecuaciones", { age: Number(age), condition, area, lang });
      setResult(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-[720px] mx-auto">
      <button className="btn-ghost btn mb-6" onClick={onBack} data-testid="gadget-back">
        <IconArrowLeft size={14} /> {t("nav.gadgets")}
      </button>
      <div className="flex items-start gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-teal-soft text-teal-deep flex items-center justify-center"><IconCalculator size={22} /></div>
        <div>
          <p className="eyebrow">PsicoGadget</p>
          <h1 className="font-display text-[26px] font-medium">
            {lang === "es" ? "Calculadora de adecuaciones" : "Accommodation calculator"}
          </h1>
        </div>
      </div>
      <div className="card mt-6">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="field"><label>{lang === "es" ? "Edad" : "Age"}</label><input type="number" min="3" max="18" value={age} onChange={(e) => setAge(e.target.value)} className="input" data-testid="adec-age" /></div>
          <div className="field"><label>{lang === "es" ? "Condición" : "Condition"}</label>
            <select className="select-css" value={condition} onChange={(e) => setCondition(e.target.value)} data-testid="adec-cond">
              <option>TDAH</option><option>TEA</option><option>Dislexia</option><option>Discalculia</option>
            </select>
          </div>
          <div className="field"><label>{lang === "es" ? "Área" : "Area"}</label>
            <select className="select-css" value={area} onChange={(e) => setArea(e.target.value)} data-testid="adec-area">
              <option>Lectura</option><option>Escritura</option><option>Matemáticas</option><option>Conducta y regulación</option><option>General</option>
            </select>
          </div>
        </div>
        <button className="btn mt-2" onClick={submit} disabled={loading} data-testid="adec-submit">
          <IconCheck size={14} /> {loading ? "..." : (lang === "es" ? "Generar adecuación" : "Generate")}
        </button>
        {error && <p className="text-errtext text-[13px] mt-3">{error}</p>}
      </div>

      {result && (
        <div className="card mt-4" data-testid="adec-result">
          <p className="eyebrow">{result.condition} · {result.area} · {result.age} {lang === "es" ? "años" : "yo"}</p>
          <ul className="mt-4 space-y-2">
            {result.adecuaciones.map((a, i) => (
              <li key={i} className="text-[14px] leading-relaxed flex gap-2">
                <span className="text-honey">•</span> {a}
              </li>
            ))}
          </ul>
          <p className="text-tert text-[12px] mt-4 italic">{lang === "es" ? result.note_es : result.note_en}</p>
        </div>
      )}
    </div>
  );
}
